import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Stack,
  Switch,
  Typography,
} from "@altimateai/lego";
import ClusterRecommendationDag from "./ClusterRecommendationDag";
import type { ClusterAutoTuneRecommendation, ClusterRecType } from "./types";

// Static copy for the two cluster sub-toggles. The order here is the
// render order; only rows whose ``rec_type`` is present in the cluster's
// active recommendations are shown.
const REC_TYPE_META: { recType: ClusterRecType; title: string; description: string }[] = [
  {
    recType: "resize",
    title: "Worker Sizing",
    description:
      "Minimum worker downsizing — automatically reduces minimum worker nodes based on usage patterns.",
  },
  {
    recType: "auto_terminate",
    title: "Auto Termination Minutes",
    description:
      "Automatically lowers a cluster's autotermination_minutes to match its actual idle pattern.",
  },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clusterName: string;
  // Identifies the cluster + its node types for the recommended-state
  // DAG card. The node types are unchanged by the recommendation, shown
  // only as context on the card.
  clusterId: string;
  workerNodeType?: string | null;
  driverNodeType?: string | null;
  // Live worker sizing — fallback for the DAG card when the cluster has
  // no resize recommendation (so it shows the real count, not "—").
  minAutoscaleWorkers?: number | null;
  maxAutoscaleWorkers?: number | null;
  workerCount?: number | null;
  // Preflight gate — when blocked, every switch is disabled and the
  // actionable message is shown so the user knows why.
  blocked?: boolean;
  blockedMessage?: string | null;
  recommendations: ClusterAutoTuneRecommendation[];
  // True while the recommendations query is still resolving — keeps the
  // empty-state message from flashing before the recs load.
  isLoadingRecs?: boolean;
  isToggling?: boolean;
  onToggleRec: (recType: ClusterRecType, enabled: boolean) => void;
}

const SWITCH_CLASS = "data-[state=checked]:!al-bg-[#004FBF]";

// Settings drawer for cluster Auto Tune. Each sub-toggle (Worker Sizing /
// Auto Termination) maps to a recommendation ``rec_type`` and persists
// immediately (config + audit) — no Save/Apply, and no Databricks edit is
// invoked. The master enable/disable lives in the page-header control, not
// here. ``Cancel`` only closes the drawer (changes are already recorded).
// All switches are disabled when the permission preflight blocks.
const ClusterAutoTuneSettingsDrawer = ({
  open,
  onOpenChange,
  clusterName,
  clusterId,
  workerNodeType,
  driverNodeType,
  minAutoscaleWorkers,
  maxAutoscaleWorkers,
  workerCount,
  blocked = false,
  blockedMessage,
  recommendations,
  isLoadingRecs = false,
  isToggling = false,
  onToggleRec,
}: Props) => {
  const enabledByRecType = new Map(recommendations.map(rec => [rec.rec_type, rec.enabled]));
  // Only render a row for a rec_type that actually exists in the cluster's
  // recommendations — never show a toggle the cluster has no recommendation for.
  const rows = REC_TYPE_META.filter(meta => enabledByRecType.has(meta.recType));
  const disabled = blocked || isToggling;
  const hasRecs = rows.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="!al-w-1/2 !al-max-w-[700px] al-z-[100]">
        <SheetHeader>
          <SheetTitle>Auto Tune Settings</SheetTitle>
          <SheetDescription>Cluster: {clusterName}</SheetDescription>
        </SheetHeader>

        <Stack direction="column" gap="md" className="al-mt-6">
          {!hasRecs ? (
            isLoadingRecs ? null : (
              <div className="al-rounded-lg al-border al-border-stroke-light al-p-4">
                <Typography size="sm" className="al-text-muted-foreground">
                  No Auto Tune recommendations are available for this cluster yet.
                </Typography>
              </div>
            )
          ) : (
            <>
              {blocked && blockedMessage ? (
                <div className="al-rounded-lg al-border al-border-red-200 al-bg-red-50 al-p-3">
                  <Typography size="sm" className="al-text-red-700">
                    {blockedMessage}
                  </Typography>
                </div>
              ) : null}

              {rows.map(meta => (
                <Stack
                  key={meta.recType}
                  direction="row"
                  align="start"
                  justify="between"
                  gap="md"
                  className="al-rounded-lg al-border al-border-stroke-light al-p-4"
                >
                  <Stack direction="column" gap="xxs" className="al-flex-1">
                    <Typography weight="medium">{meta.title}</Typography>
                    <Typography size="sm" className="al-text-muted-foreground">
                      {meta.description}
                    </Typography>
                  </Stack>
                  <Switch
                    checked={Boolean(enabledByRecType.get(meta.recType))}
                    disabled={disabled}
                    onCheckedChange={next => onToggleRec(meta.recType, next)}
                    className={SWITCH_CLASS}
                    aria-label={`${meta.title} toggle`}
                  />
                </Stack>
              ))}

              {/* Recommended-state DAG — one card showing this cluster's
                  Current → Proposed config, reusing the jobs Auto Tune
                  canvas. Renders nothing when no actionable rec exists. */}
              <ClusterRecommendationDag
                clusterId={clusterId}
                clusterName={clusterName}
                workerNodeType={workerNodeType}
                driverNodeType={driverNodeType}
                minAutoscaleWorkers={minAutoscaleWorkers}
                maxAutoscaleWorkers={maxAutoscaleWorkers}
                workerCount={workerCount}
                recommendations={recommendations}
              />
            </>
          )}
        </Stack>

        <SheetFooter className="al-mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default ClusterAutoTuneSettingsDrawer;
